import React from 'react'
import { Row, Col, Card, Button, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap'

// components
import * as Messages from '../../ui/messages/messages'
import { RefreshCw } from 'react-feather'
import { OrgMappingContent } from './components/OrgMappingContent'
import { OrgMappingHeader } from './components/OrgMappingHeader'

// definitions
import * as AppDefinitions from '../../../App.d'
import { AccountStripeOrgWithData, OrgSettings } from '../../../screens/subsync/subsync'
import {
	StripeOrgAccountXeroOrg,
	StripeOrgAccountXeroOrgMapping,
	StripeProductXeroMapping,
	StripeRule,
	StripeTaxRateXeroTaxRate,
	XeroAPIContact,
	XeroContact,
} from '../../../../../back-end/utilities/apiDefinitions'

// utilities
import * as Request from '../../../utilities/request'

// helper functions
import * as functions from './functions'
import { StripeOrgAccountXeroOrgSettings } from '../../../../../back-end/common/stripeOrgAccountXeroOrgSettings'

interface SettingsComponentFrameProps {
	show: boolean
	appState: AppDefinitions.AppState
	orgSettings: OrgSettings
	updateOrgSettings: (orgSettings: OrgSettings) => void
	refreshData: () => void
}

interface EditedMarker {
	edited?: boolean
}

interface DetailedStripeOrgAccountXeroOrg extends StripeOrgAccountXeroOrg {
	edited?: boolean
	stripeProductXeroMappings?: (StripeProductXeroMapping & EditedMarker)[]
	stripeTaxRateXeroTaxRates?: (StripeTaxRateXeroTaxRate & EditedMarker)[]
	customMappings?: (Omit<StripeOrgAccountXeroOrgMapping, 'stripeRules'> &
		EditedMarker & {
			stripeRules?: (StripeRule & EditedMarker)[]
		})[]
	stripeOrgAccountXeroOrgSettings?: StripeOrgAccountXeroOrgSettings & {
		stripeFeesXeroContactXeroID?: string
		stripeFeesXeroContact?: XeroAPIContact
		rollupsXeroContactXeroID?: string
		rollupsXeroContact?: XeroAPIContact
		paymentsXeroContactXeroID?: string
		paymentsXeroContact?: XeroAPIContact
		ignorePreSubsyncInvoicesEnabled?: boolean
	}
}

export interface Error extends functions.ValidationError {
	stripeID: string
}

const SettingsComponent = (props: SettingsComponentFrameProps) => {
	const [messages, updateMessages] = Messages.useMessageReducer([])
	const [orgMappings, updateOrgMappings] = React.useState<DetailedStripeOrgAccountXeroOrg[]>(props.orgSettings.orgMappings)
	const [submitting, setSubmitting] = React.useState<boolean>(false)
	const [errors, updateErrors] = React.useState<Error[]>([])

	React.useEffect(() => {
		updateOrgMappings(props.orgSettings.orgMappings)
	}, [props.orgSettings.orgMappings])

	const handleSave = async (stripeOrg: AccountStripeOrgWithData) => {
		setSubmitting(true)
		const validationErrors = functions.validateMappings(
			[stripeOrg],
			orgMappings.filter((mapping) => mapping.edited && mapping.stripeOrgID === stripeOrg.stripeOrg!.stripeOrgID),
			props.orgSettings.stripeObjectMaps
		)
		updateErrors([])
		if ((errors.length === 0 && validationErrors.length > 0) || validationErrors.filter((errors) => errors.severity === 'error').length > 0) {
			updateErrors(validationErrors)
			if (validationErrors.filter((errors) => errors.severity === 'error').length > 0) {
				updateMessages({
					type: 'add',
					data: {
						severity: 'danger',
						message: `Unable to update account mappings.\n\n ${validationErrors.map((e) => e.message).join(', ')}`,
						timeout: 3000,
						dismissible: true,
					},
				})
			}
		} else {
			const filteredMappings = functions.filterUneditedMappings(orgMappings.filter((mapping) => mapping.stripeOrgID === stripeOrg.stripeOrg!.stripeOrgID))
			// remove hardcoded 'not in use' option
			const mappings = filteredMappings.map((mapping) => ({
				...mapping,
				stripeTaxRateXeroTaxRates: mapping.stripeTaxRateXeroTaxRates!.map((taxRateMapping) =>
					taxRateMapping.xeroTaxRateID === 'N/A' ? { ...taxRateMapping, xeroTaxRateID: null } : taxRateMapping
				),
				stripeProductXeroMappings: mapping.stripeProductXeroMappings!.map((productMapping) => ({
					...productMapping,
					...(productMapping.xeroAccountID === 'N/A' ? { xeroAccountID: null } : {}),
					...(productMapping.xeroItemID === 'N/A' ? { xeroItemID: null } : {}),
				})),
				xeroOrgID: props.orgSettings.xeroOrgs.find((xo) => xo.accountXeroOrgID === mapping.accountXeroOrgID)?.xeroOrg?.xeroOrgID,
				customMappings: mapping.customMappings?.filter(
					(customMapping) => customMapping.type === 'TrackingCategoryOption' || mapping.stripeOrgAccountXeroOrgMappingType === 'Custom'
				), // only include custom mappings if custom mapping type selected, as the validation won't have run, and they could be incomplete
				stripeOrgAccountXeroOrgSettings: {
					...mapping.stripeOrgAccountXeroOrgSettings,
					...(mapping.stripeOrgAccountXeroOrgSettings?.customMappingDefaultXeroAccountID === 'N/A'
						? { customMappingDefaultXeroAccountID: null }
						: {}),
					...(mapping.stripeOrgAccountXeroOrgSettings?.customMappingDefaultXeroItemID === 'N/A' ? { customMappingDefaultXeroItemID: null } : {}),
				},
			}))
			const res = await Request.put('stripeorgaccountxeroorg', mappings, props.appState.authState)
			if (res.status === 200) {
				updateMessages({
					type: 'add',
					data: {
						severity: 'success',
						message:
							'Successfully updated account mappings.' +
							(validationErrors.length > 0 ? '\n\nSome mappings are incomplete and can cause issues.' : ''),
						timeout: 3000,
						dismissible: true,
					},
				})
				// Get updated Xero contacts in case a new one was just created
				const contactsRes = await Request.get('xerocontact', props.appState.authState)
				props.updateOrgSettings({
					...props.orgSettings,
					orgMappings: res.data.stripeOrgAccountXeroOrgs.map((orgMapping: StripeOrgAccountXeroOrg) => ({
						...orgMapping,
						stripeTaxRateXeroTaxRates: orgMapping.stripeTaxRateXeroTaxRates!.map((strxtr) =>
							strxtr.xeroTaxRateID === null ? { ...strxtr, xeroTaxRateID: 'N/A' } : strxtr
						),
						stripeProductXeroMappings: orgMapping.stripeProductXeroMappings!.map((spxm) =>
							!spxm.ignoreInvoices && spxm.xeroAccountID === null && spxm.xeroItemID === null
								? { ...spxm, xeroAccountID: 'N/A', xeroItemID: 'N/A' }
								: spxm
						),
						stripeOrgAccountXeroOrgSettings: {
							...orgMapping.stripeOrgAccountXeroOrgSettings,
							...(orgMapping.stripeOrgAccountXeroOrgMappingType === 'Custom' &&
							orgMapping.stripeOrgAccountXeroOrgSettings?.customMappingDefaultXeroItemID === null &&
							orgMapping.stripeOrgAccountXeroOrgSettings?.customMappingDefaultXeroAccountID === null
								? { customMappingDefaultXeroItemID: 'N/A', customMappingDefaultXeroAccountID: 'N/A' }
								: {}),
						},
					})),
					xeroOrgs: props.orgSettings.xeroOrgs.map((xo) => ({
						...xo,
						contacts: contactsRes.data.xeroContacts.filter((contact: XeroContact) => contact.xeroOrgID === xo.xeroOrg?.xeroOrgID),
					})),
				})
			} else {
				updateMessages({
					type: 'add',
					data: {
						severity: 'danger',
						message: 'Unable to update account mappings',
						timeout: 3000,
						dismissible: true,
					},
				})
			}
		}
		setSubmitting(false)
	}

	const accountStripeOrgID = location.hash.substring(10)
	const currentSettings = props.orgSettings.stripeOrgs.find((so) => so.accountStripeOrgID === accountStripeOrgID)
	if (!currentSettings) {
		return null
	}

	const orgMapping = orgMappings.find((mapping) => mapping.stripeOrgID === currentSettings.stripeOrg!.stripeOrgID)
	const mappedOrg = props.orgSettings.xeroOrgs.find((xo) => xo.accountXeroOrgID === orgMapping?.accountXeroOrgID)

	if (!props.show) {
		return null
	}

	const highestError = errors.some((error) => error.severity === 'error') ? 'error' : errors.some((error) => error.severity === 'warning') ? 'warning' : null

	return (
		<>
			<Card className="subsync-card mb-2">
				<Card.Header>
					<Row>
						<Col style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '15px' }}>
							<h3>Account Mapping</h3>
						</Col>
						<Col sm="auto">
							<OverlayTrigger placement="auto" overlay={<Tooltip id={`tooltip-remove-connection`}>Resync from Xero</Tooltip>}>
								<Button type="submit" style={{ border: '1px solid black' }} onClick={() => props.refreshData()}>
									Sync &#8192; <RefreshCw />
								</Button>
							</OverlayTrigger>
						</Col>
					</Row>
				</Card.Header>
				<Card.Body>
					<Row className="subsync-card-inner-row">
						<OrgMappingHeader
							stripeOrg={currentSettings}
							orgMappings={orgMappings}
							orgMapping={orgMapping}
							mappedXeroOrg={mappedOrg}
							xeroOrgs={props.orgSettings.xeroOrgs}
							handleOrgUpdate={(...args) => functions.handleOrgUpdate(updateOrgMappings, ...args)}
							handleOrgAdd={(...args) => functions.handleOrgAdd(orgMappings, updateOrgMappings, ...args)}
						/>
					</Row>
				</Card.Body>
			</Card>
			<OrgMappingContent
				stripeOrg={currentSettings}
				orgMapping={orgMapping!}
				mappedXeroOrg={mappedOrg}
				stripeMappingTypes={props.orgSettings.stripeMappingTypes}
				stripeObjectMaps={props.orgSettings.stripeObjectMaps}
				stripeObjectMapSelectOptions={props.orgSettings.stripeObjectMapSelectOptions}
				xeroOrgs={props.orgSettings.xeroOrgs}
				appState={props.appState}
				handleOrgUpdate={(...args) => functions.handleOrgUpdate(updateOrgMappings, ...args)}
				handleProductUpdate={(...args) => functions.handleProductUpdate(orgMappings, updateOrgMappings, ...args)}
				handleProductAdd={(...args) => functions.handleProductAdd(orgMappings, updateOrgMappings, ...args)}
				handleApplyToAllProducts={(...args) => functions.handleApplyToAllProducts(orgMappings, updateOrgMappings, currentSettings.products, ...args)}
				handleTaxRateUpdate={(...args) => functions.handleTaxRateUpdate(orgMappings, updateOrgMappings, ...args)}
				handleTaxRateAdd={(...args) => functions.handleTaxRateAdd(orgMappings, updateOrgMappings, ...args)}
				handleSettingsUpdate={(...args) => functions.handleSettingsUpdate(orgMappings, updateOrgMappings, ...args)}
				handleCustomMappingUpdate={(...args) => functions.handleCustomMappingUpdate(orgMappings, updateOrgMappings, ...args)}
				updateMessages={updateMessages}
				errors={errors}
			/>
			{highestError === 'error' && (
				<Row>
					<Col />
					<Col>
						<p className="error p-3">You have some errors. Fix these up before you can save.</p>
					</Col>
				</Row>
			)}
			{highestError === 'warning' && (
				<Row>
					<Col />
					<Col>
						<p className="error p-3">
							You have some warnings. You can still save with these but fixing them up will help resolve possible errors in the future.
						</p>
					</Col>
				</Row>
			)}
			<Row>
				<Col />
				<Col sm="auto">
					<div className="p-3">
						<Button onClick={() => handleSave(currentSettings)} variant={'dark'}>
							{submitting ? <Spinner animation={'border'} size={'sm'} /> : `Save ${highestError === 'warning' ? 'with warnings' : 'settings'}`}
						</Button>
					</div>
				</Col>
			</Row>
			<Messages.Messages messages={messages} updateMessage={updateMessages} />
		</>
	)
}

export default SettingsComponent
export type { DetailedStripeOrgAccountXeroOrg }
